import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  User, 
  Mail, 
  Lock, 
  Building2, 
  Phone, 
  MapPin,
  ArrowRight, 
  Loader2,
  ArrowLeft,
  Wrench,
  ShoppingCart,
  CheckCircle,
  Droplets,
  Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PlatformChatBot from "@/components/PlatformChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Business categories
const BUSINESS_CATEGORIES = [
  {
    id: "truck_repair",
    name: "Repair Shop",
    description: "Auto repair, mechanic, truck repair businesses",
    icon: Wrench,
    color: "blue"
  },
  {
    id: "grocery_store",
    name: "Grocery Store / Market",
    description: "Grocery stores, supermarkets, convenience stores",
    icon: ShoppingCart,
    color: "green"
  },
  {
    id: "wholesale",
    name: "Wholesale / Import-Export",
    description: "Wholesale distribution, import/export trading",
    icon: Globe,
    color: "emerald"
  },
  {
    id: "pressure_washing",
    name: "Pressure Washing",
    description: "Residential + commercial exterior cleaning (map-based sqft quotes)",
    icon: Droplets,
    color: "cyan",
    badge: "NEW"
  }
];

// High-Quality Logo Component (same as Login page)
const BusinessPortalLogo = ({ size = 48 }) => {
  const primaryColor = "#3B82F6";
  const secondaryColor = "#8B5CF6";
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 48 48" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="logoGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={primaryColor} />
          <stop offset="100%" stopColor="#818CF8" />
        </linearGradient>
        <linearGradient id="logoGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={secondaryColor} />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="4" fill="url(#logoGrad1)" filter="url(#glow)" />
      <rect x="26" y="2" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" filter="url(#glow)" />
      <rect x="2" y="26" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" filter="url(#glow)" />
      <rect x="26" y="26" width="20" height="20" rx="4" fill="url(#logoGrad1)" filter="url(#glow)" />
      <line x1="22" y1="12" x2="26" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="12" y1="22" x2="12" y2="26" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="36" y1="22" x2="36" y2="26" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="22" y1="36" x2="26" y2="36" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
};

// Animated Background Component
const AnimatedBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div 
        className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 animate-pulse bg-blue-400"
        style={{ 
          top: '-10%', 
          left: '-10%',
          animation: 'float1 15s ease-in-out infinite'
        }} 
      />
      <div 
        className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15 bg-purple-400"
        style={{ 
          top: '50%', 
          right: '-5%',
          animation: 'float2 18s ease-in-out infinite'
        }} 
      />
      <div 
        className="absolute w-[400px] h-[400px] rounded-full blur-3xl opacity-10 bg-indigo-300"
        style={{ 
          bottom: '-10%', 
          left: '30%',
          animation: 'float3 20s ease-in-out infinite'
        }} 
      />
      
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, 50px) scale(1.05); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, -30px) scale(1.1); }
          66% { transform: translate(20px, -50px) scale(0.9); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, -20px) scale(1.05); }
          66% { transform: translate(-30px, 40px) scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = category, 2 = business info, 3 = account info
  const [signupComplete, setSignupComplete] = useState(null); // {email, businessName} after success
  const [resendingVerify, setResendingVerify] = useState(false);
  
  const [formData, setFormData] = useState({
    business_category: "",
    business_name: "",
    business_phone: "",
    business_address: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    confirm_password: ""
  });

  const handleCategorySelect = (categoryId) => {
    setFormData({ ...formData, business_category: categoryId });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.business_category) {
        toast.error("Please select a business category");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.business_name.trim()) {
        toast.error("Please enter your business name");
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.owner_name || !formData.owner_email || !formData.owner_password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.owner_password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.owner_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        business_category: formData.business_category,
        business_name: formData.business_name.trim(),
        business_phone: formData.business_phone.trim(),
        business_address: formData.business_address.trim(),
        owner_name: formData.owner_name.trim(),
        owner_email: formData.owner_email.toLowerCase().trim(),
        owner_password: formData.owner_password
      });

      // Show "check your email" confirmation in-page (don't auto-login —
      // the account is unverified and login will be blocked until they
      // click the verification link in their inbox).
      setSignupComplete({
        email: response.data.email,
        businessName: response.data.business_name,
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerify = async () => {
    if (!signupComplete?.email) return;
    setResendingVerify(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: signupComplete.email });
      toast.success("Verification email sent again. Check your inbox.");
    } catch (e) {
      toast.error("Couldn't resend. Please try again.");
    } finally {
      setResendingVerify(false);
    }
  };

  // After successful signup → show check-your-email confirmation instead of
  // auto-logging in. Login is blocked until they click the link.
  if (signupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative">
        <AnimatedBackground />
        <Card className="w-full max-w-lg bg-white/95 backdrop-blur-sm border-slate-200 shadow-xl relative z-10" data-testid="signup-success-card">
          <CardContent className="p-8 text-center space-y-5">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Check your email</h2>
            <p className="text-slate-600">
              We sent a verification link to{" "}
              <span className="font-semibold text-slate-900">{signupComplete.email}</span>.
              Click it to activate your <span className="font-semibold">{signupComplete.businessName}</span> account.
            </p>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 text-left">
              <p className="font-semibold text-slate-700 mb-1">Didn't get it?</p>
              <p>• Check your spam folder.</p>
              <p>• Make sure {signupComplete.email} is correct.</p>
              <p>• Click "Resend" below.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleResendVerify}
                disabled={resendingVerify}
                className="flex-1"
                data-testid="resend-verification-btn"
              >
                {resendingVerify ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend email"}
              </Button>
              <Button
                onClick={() => navigate("/login")}
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white"
                data-testid="go-to-login-btn"
              >
                Go to login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative">
      {/* Animated Background */}
      <AnimatedBackground />
      
      <Card className="w-full max-w-lg bg-white/95 backdrop-blur-sm border-slate-200 shadow-xl relative z-10">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <BusinessPortalLogo size={64} />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Get Your Business Online
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Manage invoices, inventory & customers — all in one place
          </p>
          <p className="text-sm text-green-600 font-medium mt-2">
            Free to start. No credit card required.
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-slate-300'}`} />
            <div className={`w-8 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-300'}`} />
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-slate-300'}`} />
            <div className={`w-8 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-slate-300'}`} />
            <div className={`w-3 h-3 rounded-full ${step >= 3 ? 'bg-blue-600' : 'bg-slate-300'}`} />
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Step {step} of 3: {step === 1 ? "Business Type" : step === 2 ? "Business Info" : "Your Account"}
          </p>
        </CardHeader>
        
        <CardContent className="pt-4">
          {step === 1 ? (
            // Step 1: Category Selection
            <div className="space-y-4">
              <p className="text-sm text-slate-600 text-center mb-4">What type of business do you have?</p>
              
              <div className="grid gap-3">
                {BUSINESS_CATEGORIES.map((category) => {
                  const Icon = category.icon;
                  const isSelected = formData.business_category === category.id;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategorySelect(category.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>
                          {category.name}
                          {category.badge && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 border border-cyan-300 align-middle">
                              {category.badge}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">{category.description}</p>
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-6 h-6 text-blue-500" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2 mt-4">
                <Link to="/login" className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" /> Back to Login
                  </Button>
                </Link>
                <Button
                  onClick={handleNext}
                  disabled={!formData.business_category}
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="next-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          ) : step === 2 ? (
            // Step 2: Business Information
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Business Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="e.g., ABC Mechanic Shop"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="pl-10 h-12 text-lg bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="business-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">Business Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="(555) 555-5555"
                    value={formData.business_phone}
                    onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="business-phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">Business Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="123 Main St, City, State 12345"
                    value={formData.business_address}
                    onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="business-address-input"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 h-12 border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!formData.business_name.trim()}
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="next-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            // Step 3: Account Information
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Your Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="John Doe"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="owner-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="owner-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={formData.owner_password}
                    onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="owner-password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="password"
                    placeholder="Re-enter your password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 h-12 border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" /> Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.owner_name || !formData.owner_email || !formData.owner_password}
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="create-account-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Create Account
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
          
          <div className="text-center pt-4 mt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
          
          <div className="text-center pt-4 mt-2 flex justify-center gap-4 text-xs text-slate-400">
            <Link to="/privacy" className="hover:text-blue-600 hover:underline">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-blue-600 hover:underline">Terms of Service</Link>
          </div>
        </CardContent>
      </Card>
      
      {/* Platform Chatbot */}
      <PlatformChatBot />
    </div>
  );
}
